import React, { useEffect, useMemo, useState } from "react";
import { Form, Table } from "antd";
import { Button, Message, Popover, Tag } from "../../../../BP2Components";
import IntlMessages from "@util/IntlMessages";
import GridElementRenderer from "./GridElementRenderer";
import { useDispatch, useSelector } from "react-redux";
import { addGridFormRef, removeGridFormRef } from "../../../../../appRedux/actions/FormsV2";
import { useIntl } from "react-intl";
import "./Grid.less";
import { getUpdatedGridRows, getSqlFieldValues } from "../../../../../services/restAPI/RestApi";
import { useFormContext } from "../../../../../context/FormContext/FormContext";
import { addSubformData } from "../../../../../services/api/Api";
import { handleInitialSqlValues, transformFormValuesForRequest } from "../../../GeneralScripts";
import { Icon_Delete } from "../../../../BP2Components/Icons/icons";
import projectConfig from "../../../../../projectConfig.json";

const EditableContext = React.createContext();

const EditableRow = ({ form, index, mainRecordId, ...props }) => {
   const dispatch = useDispatch();

   useEffect(() => {
      // This will add gridFormRef on the initial render of the component
      // allowing us to validate it later
      dispatch(addGridFormRef({ mainRecordId, gridFormRef: form }));

      return () => dispatch(removeGridFormRef(mainRecordId));
   }, []);

   return (
      <EditableContext.Provider value={form}>
         <tr
            {...Object.fromEntries(
               Object.entries(props).map(([key, value]) => {
                  if (value && typeof value === "object" && "value" in value) {
                     return [key, value.value];
                  } else {
                     return [key, value];
                  }
               }),
            )}
         />
      </EditableContext.Provider>
   );
};
const EditableFormRow = Form.create()(EditableRow);

// This needs to be a class component, because antd needs render function from this class
class EditableCell extends React.Component {
   renderCell = (form) => {
      const { record, column, gridFormId, editableElement, tableDisabled, isEdited } = this.props;

      if (editableElement && isEdited) {
         return (
            <GridElementRenderer
               element={editableElement}
               form={form}
               tableDisabled={tableDisabled}
               gridFormId={gridFormId}
               isEdited={isEdited}
               rowDataId={record.mainRecordId}
            />
         );
      }

      // Here we only display the value/label of the field. We need to make sure that value is properly handled
      // so saving form works correctly
      const label = record?.[column]?.label || record?.[column]?.value;
      return (
         <Form.Item style={{ wordBreak: "keep-all", whiteSpace: "nowrap" }}>
            {form.getFieldDecorator(column, { initialValue: label })(<span>{label}</span>)}
         </Form.Item>
      );
   };

   render() {
      const { record, children, editableElement, ...restProps } = this.props;
      return (
         <td {...restProps}>
            {record ? (
               <EditableContext.Consumer>{this.renderCell}</EditableContext.Consumer>
            ) : (
               children
            )}
         </td>
      );
   }
}

var numberOfEdits = {}; // FIRST ROW EDIT DOESNT SEE rows wtf ????????????????
var globalRowChanges = {}; // FIRST ROW EDIT DOESNT SEE rows wtf ????????????????
// TODO - propably there will be a problem with double grids

const Grid = ({
   form,
   formElements,
   rows,
   disabled,
   gridFormId,
   formUuid,
   hideManagementButtons,
   mainForm,
   fetchedData,
}) => {
   const intl = useIntl();
   const [dataSource, setDataSource] = useState([]);
   const [selectedRows, setSelectedRows] = useState([]);
   const [editedRows, setEditedRows] = useState(new Map());
   const [loadingRows, setLoadingRows] = useState([]);

   const { gridFormKeys, setGridFormKeys, addRowOptions, setGridRowChanges, gridRowChanges } =
      useFormContext();

   const taskDurationTechName = "ProjectTask_time_consuming_1bbglr";
   const isSsr_deleted = formElements.some((element) => element.tech_name === "ssr_deleted");

   const handleDelete = () => {
      try {
         if (isSsr_deleted) {
            setDataSource((prevDataSource) =>
               prevDataSource.map((item) =>
                  selectedRows.includes(item.key)
                     ? {
                          ...item,
                          ssr_deleted: {
                             ...item.ssr_deleted,
                             value: "1",
                          },
                       }
                     : item,
               ),
            );
         } else {
            setDataSource((prevDataSource) =>
               prevDataSource.filter((item) => !selectedRows.includes(item.key)),
            );
         }
         setSelectedRows([]);
      } catch {
         Message.error(intl.formatMessage({ id: "forms.errorDeletingRow" }));
      }
   };

   const handleAdd = () => {
      try {
         const newRowKey = dataSource.length === 0 ? 0 : dataSource[dataSource.length - 1]?.key + 1;
         const newRow = {
            key: newRowKey,
            // This is added as number, so we know that it has been added by us.
            // MainRecordId from backend comes as string
            mainRecordId: Math.floor(Math.random() * 1000000000 + 1000000000),
         };
         columns.forEach((column) => (newRow[column.dataIndex] = { value: "" }));
         setDataSource((prevDataSource) => [...prevDataSource, newRow]);
      } catch {
         Message.error(intl.formatMessage({ id: "forms.errorSettingRowKey" }));
      }
   };

   const handleRowSelection = (selectedRowKeys) => {
      setSelectedRows(selectedRowKeys);
   };

   const handleColumnSorter = (columnTitle) => ({
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => {
         const x = a[columnTitle]?.label || a[columnTitle]?.value || a[columnTitle];
         const y = b[columnTitle]?.label || b[columnTitle]?.value || b[columnTitle];

         return x > y ? -1 : 1;
      },
   });

   const handleEditedRow = (dataId, record) => async () => {
      try {
         if (editedRows.has(dataId)) return;
         setLoadingRows((prev) => [...prev, dataId]);
         /*  const originFormsData = {
            [formInfo[formUuid].formId]: transformFormValuesForRequest(
               formInfo[formUuid].mainFormRef.getFieldsValue(),
               true
            )
         }; */
         let formRequestBody = {
            ...transformFormValuesForRequest(form.getFieldsValue()),
            UIF_ID: mainForm?.forms_data?.UIF_ID,
            easyForm: true,
         };
         if (mainForm?._formKey) {
            formRequestBody["_formKey"] = mainForm?._formKey;
         }
         const resp2 = await addSubformData(gridFormId, formRequestBody);
         const rowElements = Object.values(
            resp2.data.getSubformData.mainForm.forms_data.tab[0].elements,
         );
         const rowFormKey = resp2?.data?.getSubformData?.mainForm?._formKey;
         const currentRowKeys = { ...gridFormKeys };
         currentRowKeys[dataId] = rowFormKey;
         setGridFormKeys(currentRowKeys);
         // First edit doesn't see real context gridRowChanges - wtf
         let rowChanges;
         if (numberOfEdits[gridFormId] === 0) {
            rowChanges = { ...globalRowChanges[gridFormId] };
         } else {
            rowChanges = { ...gridRowChanges };
         }
         rowChanges[`${gridFormId}_${record?.key.toString()}`] = true;
         setGridRowChanges(rowChanges);

         let updatedRowLabels = {};
         // If previously existing record
         if (fetchedData[record?.key]) {
            const updatedGridRows = await getUpdatedGridRows(
               gridFormId,
               fetchedData[record?.key],
               mainForm?._formKey,
            );
            updatedRowLabels = updatedGridRows?.data?.updateRowLabels;
         }

         const rowFields = rowElements.map((cell) => {
            const newCell = { ...cell };
            // If response has empty value we need to get the old values
            if (cell.value === "") {
               const previousCellData = record[cell?.tech_name];
               if (previousCellData && previousCellData.value) {
                  newCell.value = previousCellData.value;
               }
            }
            if (updatedRowLabels[newCell.tech_name]?.value) {
               if (cell.enum === "sql" && updatedRowLabels[newCell.tech_name]?.label) {
                  newCell.value = {
                     value: updatedRowLabels[newCell.tech_name]?.value,
                     label: updatedRowLabels[newCell.tech_name]?.label,
                  };
               } else {
                  const newVal = updatedRowLabels[newCell.tech_name]?.value;
                  if (cell.enum === "dec" && newVal !== "" && !Number.isNaN(+newVal)) {
                     const parsedVal = parseFloat(newVal);
                     newCell.value = parsedVal.toFixed(cell.precision);
                  } else {
                     newCell.value = newVal;
                  }
               }
            }
            return newCell;
         });
         const rowDataElements = rowFields.reduce((acc, element) => {
            acc[element.tech_name] = element;
            return acc;
         }, {});

         handleInitialSqlValues(rowFields, dataId);
         setEditedRows((prev) => {
            const newData = new Map(prev);
            newData.set(dataId, rowDataElements);
            return newData;
         });
         setLoadingRows((prev) => [...prev.filter((item) => item !== dataId)]);

         const valuesFromRow = {};
         Object.keys(rowDataElements).forEach((key) => {
            valuesFromRow[key] = rowDataElements[key].value;
         });
         const defaultFields = mainForm?.forms_data?.defaultSqlFields;
         const sqlFields = mainForm?.forms_data?.sqlFields;

         const valuesFromForm = {
            ...valuesFromRow,
            easyForm: true,
            UIF_ID: gridFormId,
         };
         getSqlFieldValues(defaultFields, sqlFields, valuesFromForm, mainForm?._formKey).then(
            (res) => {
               const currentRowOptions = res?.data?.getAllSqls;
               addRowOptions(dataId, currentRowOptions);
            },
         );
      } catch {
         setLoadingRows((prev) => [...prev.filter((item) => item !== dataId)]);
         Message.error(intl.formatMessage({ id: "forms.errorEditingRow" }));
      }
      numberOfEdits[gridFormId] = numberOfEdits[gridFormId] + 1;
   };

   const columns = useMemo(() => {
      if (!formElements.length) return [];
      const fetchedColumns = Object.values(formElements).map((elementData) => ({
         title: elementData.label,
         dataIndex: elementData.tech_name,
         key: elementData.tech_name,
         className:
            elementData.hidden === "1"
               ? "ant-table-header-column hidden"
               : "ant-table-header-column",
         onCell: (record, index) => ({
            record,
            index,
            gridFormId,
            editableElement: editedRows.get(record.mainRecordId)?.[elementData.tech_name],
            column: elementData.tech_name,
            tableDisabled: disabled,
            isEdited: editedRows.has(record.mainRecordId),
            isLoading: loadingRows.includes(record.mainRecordId),
         }),
         ...handleColumnSorter(elementData.tech_name),
      }));
      if (disabled) {
         return fetchedColumns;
      } else {
         fetchedColumns.unshift({
            dataIndex: "gridActions",
            key: "gridActions",
            render: (_, record) => {
               if (!editedRows.has(record.mainRecordId))
                  return (
                     <Popover content={intl.formatMessage({ id: "global.edit" })}>
                        <span
                           className="span-style icon icon-edit"
                           onClick={handleEditedRow(record.mainRecordId, record)}
                        />
                     </Popover>
                  );
            },
         });
         return fetchedColumns;
      }
   }, [formElements.length, editedRows.size, disabled, loadingRows.length]);

   useEffect(() => {
      setDataSource(rows);
   }, [JSON.stringify(rows)]);

   useEffect(() => {
      const rowChanges = {};
      numberOfEdits[gridFormId] = 0;
      rows.forEach((row) => {
         if (row?.mainRecordId && (row?.key || row?.key === 0)) {
            rowChanges[`${gridFormId}_${row.key.toString()}`] = row.existingRecord;
         }
      });
      setGridRowChanges(rowChanges);
      globalRowChanges[gridFormId] = rowChanges;
   }, [JSON.stringify(rows)]);

   const taskDurationSum = useMemo(() => {
      const sum = dataSource
         ? dataSource.reduce((acc, item) => {
              const value = parseFloat(item[taskDurationTechName]?.value || 0);
              return acc + (isNaN(value) ? 0 : value);
           }, 0)
         : 0;

      return !isNaN(sum) ? sum.toFixed(2) : intl.formatMessage({ id: "global.error" });
   }, [dataSource]);

   const recalculateTaskDuration = (event, record) => {
      if (event.target.closest(`.field_${taskDurationTechName}`)) {
         const updatedDataSource = dataSource.map((item) => {
            if (item.mainRecordId === record.mainRecordId) {
               return {
                  ...item,
                  [taskDurationTechName]: { value: event.target.value },
               };
            }
            return item;
         });

         setDataSource(updatedDataSource);
      }
   };

   return (
      <div className={`bp2-grid-container`}>
         {hideManagementButtons !== "1" && (
            <div className="edit-buttons">
               <Button buttonType="primary" onClick={handleDelete}>
                  <Icon_Delete />
               </Button>
               <Button buttonType="primary" onClick={handleAdd}>
                  <IntlMessages id="forms.addRow" />
               </Button>
            </div>
         )}
         <Table
            className="grid-field"
            components={{ body: { row: EditableFormRow, cell: EditableCell } }}
            rowSelection={
               hideManagementButtons !== "1" && {
                  selectedRowKeys: selectedRows,
                  onChange: handleRowSelection,
               }
            }
            dataSource={dataSource}
            columns={columns}
            size="middle"
            loading={!columns.length}
            scroll={{ x: true }}
            onRow={(record, index) => ({
               ...record,
               index,
               onChange: (event) => recalculateTaskDuration(event, record),
            })}
            pagination={false}
            bordered
         />
         {projectConfig?.showGridSum && (
            <Tag>{`${intl.formatMessage({
               id: "forms.taskDurationSum",
            })}: ${taskDurationSum}`}</Tag>
         )}
      </div>
   );
};

export default Grid;
